import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';

interface Ticket {
  id: string;
  ticket_code: string;
  user_name: string;
  user_email: string;
  subject: string;
  message: string;
  status: 'open' | 'answered' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  category: string;
  admin_reply: string | null;
  admin_notes: string | null;
  user_comment: string | null;
  created_at: string;
  updated_at: string;
}

interface TicketEvent {
  id: string;
  ticket_id: string;
  event_type: string;
  actor: string;
  old_status: string | null;
  new_status: string | null;
  note: string | null;
  created_at: string;
}

const CATEGORIES = ['general', 'billing', 'technical', 'account', 'api', 'other'] as const;
type Category = typeof CATEGORIES[number];

const categoryColor: Record<string, string> = {
  general:   'bg-gray-100 text-gray-600',
  billing:   'bg-emerald-50 text-emerald-700',
  technical: 'bg-violet-50 text-violet-700',
  account:   'bg-cyan-50 text-cyan-700',
  api:       'bg-rose-50 text-rose-700',
  other:     'bg-gray-100 text-gray-500',
};

const categoryIcon: Record<string, string> = {
  general:   'ri-customer-service-2-line',
  billing:   'ri-bank-card-line',
  technical: 'ri-code-box-line',
  account:   'ri-user-settings-line',
  api:       'ri-terminal-box-line',
  other:     'ri-more-2-line',
};

const statusColor: Record<string, string> = {
  open:     'bg-amber-50 text-amber-600 border border-amber-200',
  answered: 'bg-green-50 text-green-600 border border-green-200',
  closed:   'bg-gray-100 text-gray-500 border border-gray-200',
};

const priorityColor: Record<string, string> = {
  low:    'bg-gray-100 text-gray-500',
  normal: 'bg-sky-50 text-sky-600',
  high:   'bg-orange-50 text-orange-600',
  urgent: 'bg-red-50 text-red-600 font-bold',
};

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

function getElapsed(createdAt: string): { hours: number; label: string } {
  const ms = Date.now() - new Date(createdAt).getTime();
  const hours = ms / 3_600_000;
  if (hours < 1) return { hours, label: `${Math.round(hours * 60)}m ago` };
  if (hours < 24) return { hours, label: `${Math.floor(hours)}h ago` };
  return { hours, label: `${Math.floor(hours / 24)}d ago` };
}

function SLABadge({ ticket }: { ticket: Ticket }) {
  const { hours, label } = getElapsed(ticket.created_at);
  if (ticket.status === 'closed') return null;
  const breach = ticket.status === 'open' && hours >= 48;
  const warn   = ticket.status === 'open' && hours >= 24 && hours < 48;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
      breach ? 'bg-red-50 text-red-600 border border-red-200'
      : warn  ? 'bg-orange-50 text-orange-500 border border-orange-200'
              : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>
      <i className={breach ? 'ri-alarm-warning-line' : 'ri-time-line'} />
      {label}
    </span>
  );
}

function ActivityTimeline({ ticketId }: { ticketId: string }) {
  const [events, setEvents] = useState<TicketEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    supabase.from('ticket_events').select('*').eq('ticket_id', ticketId).order('created_at', { ascending: true })
      .then(({ data }) => { setEvents((data ?? []) as TicketEvent[]); setLoading(false); });
  }, [ticketId]);

  const meta = (e: TicketEvent) => {
    if (e.event_type === 'priority_escalation') return { icon: 'ri-alarm-warning-line', color: 'text-red-500', bg: 'bg-red-50', label: 'Priority auto-escalated to Urgent' };
    if (e.new_status === 'open' && !e.old_status) return { icon: 'ri-add-circle-line', color: 'text-amber-500', bg: 'bg-amber-50', label: 'Ticket opened' };
    if (e.new_status === 'answered') return { icon: 'ri-checkbox-circle-line', color: 'text-green-600', bg: 'bg-green-50', label: 'Status → Answered' };
    if (e.new_status === 'closed') return { icon: 'ri-lock-line', color: 'text-gray-500', bg: 'bg-gray-100', label: 'Status → Closed' };
    if (e.event_type === 'reply') return { icon: 'ri-reply-line', color: 'text-brand-500', bg: 'bg-brand-50', label: `${e.actor} replied` };
    if (e.old_status && e.new_status) return { icon: 'ri-arrow-right-line', color: 'text-gray-500', bg: 'bg-gray-50', label: `${e.old_status} → ${e.new_status}` };
    return { icon: 'ri-information-line', color: 'text-gray-400', bg: 'bg-gray-50', label: e.event_type };
  };

  if (loading) return <div className="flex items-center gap-2 py-3 text-xs text-gray-400"><i className="ri-loader-4-line animate-spin" />Loading…</div>;
  if (events.length === 0) return <p className="text-xs text-gray-400 py-2">No activity recorded yet.</p>;

  return (
    <div className="relative pl-5">
      <div className="absolute left-[9px] top-3 bottom-3 w-px bg-gray-100" />
      <div className="space-y-4">
        {events.map(e => {
          const { icon, color, bg, label } = meta(e);
          return (
            <div key={e.id} className="relative flex gap-3 items-start">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 z-10 ${bg}`}>
                <i className={`${icon} text-[10px] ${color}`} />
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-gray-800">{label}</span>
                  <span className="text-[10px] text-gray-400 shrink-0">
                    {new Date(e.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <span className="text-[11px] text-gray-400">{e.actor}</span>
                {e.note && e.new_status !== 'open' && <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{e.note}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TicketsTab() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [adminNotesText, setAdminNotesText] = useState('');
  const [replyStatus, setReplyStatus] = useState<'open' | 'answered' | 'closed'>('answered');
  const [saving, setSaving] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [notesMsg, setNotesMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'answered' | 'closed'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | Category>('all');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'reply' | 'notes' | 'timeline'>('reply');

  // Bulk
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'close' | 'answered' | ''>('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('tickets').select('*').order('created_at', { ascending: false });
    setTickets((data ?? []) as Ticket[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = (t: Ticket) => {
    setSelected(t);
    setReplyText(t.admin_reply ?? '');
    setAdminNotesText(t.admin_notes ?? '');
    setReplyStatus(t.status === 'open' ? 'answered' : t.status);
    setMsg(null);
    setNotesMsg(null);
    setActiveTab('reply');
  };

  const logEvent = async (ticketId: string, eventType: string, actor: string, oldStatus: string | null, newStatus: string | null, note?: string) => {
    await supabase.from('ticket_events').insert([{ ticket_id: ticketId, event_type: eventType, actor, old_status: oldStatus, new_status: newStatus, note: note ?? null }]);
  };

  const saveReply = async () => {
    if (!selected) return;
    setSaving(true);
    setMsg(null);
    const { error } = await supabase.from('tickets').update({ admin_reply: replyText, status: replyStatus, updated_at: new Date().toISOString() }).eq('id', selected.id);
    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return; }

    const hasReply = replyText.trim().length > 0;
    if (hasReply) await logEvent(selected.id, 'reply', 'Support Team', selected.status, replyStatus, replyText.trim().substring(0, 200));
    else if (replyStatus !== selected.status) await logEvent(selected.id, 'status_change', 'Support Team', selected.status, replyStatus);

    try {
      fetch(`${SUPABASE_URL}/functions/v1/ticket-status-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_code: selected.ticket_code, status: replyStatus, user_email: selected.user_email, user_name: selected.user_name, subject: selected.subject, admin_reply: replyText.trim() || selected.admin_reply || '' }),
      }).catch(() => {});
    } catch { /* silent */ }

    const successMsg = hasReply ? 'Reply sent — user notified.' : replyStatus !== selected.status ? `Status → "${replyStatus}" — user notified.` : 'Ticket updated.';
    setMsg({ type: 'success', text: successMsg });
    const updated = { ...selected, admin_reply: replyText, status: replyStatus };
    setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, admin_reply: replyText, status: replyStatus } : t));
    setSelected(updated);
    setSaving(false);
    setTimeout(() => setMsg(null), 4000);
  };

  const saveNotes = async () => {
    if (!selected) return;
    setNotesSaving(true);
    setNotesMsg(null);
    const { error } = await supabase.from('tickets').update({ admin_notes: adminNotesText }).eq('id', selected.id);
    setNotesSaving(false);
    if (error) { setNotesMsg({ type: 'error', text: error.message }); return; }
    setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, admin_notes: adminNotesText } : t));
    setSelected(prev => prev ? { ...prev, admin_notes: adminNotesText } : null);
    setNotesMsg({ type: 'success', text: 'Internal notes saved — not visible to user.' });
    setTimeout(() => setNotesMsg(null), 3000);
  };

  const executeBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setBulkSaving(true);
    setBulkMsg(null);
    const ids = Array.from(selectedIds);
    const newStatus = bulkAction === 'close' ? 'closed' : 'answered';
    const { error } = await supabase.from('tickets').update({ status: newStatus, updated_at: new Date().toISOString() }).in('id', ids);
    if (error) { setBulkMsg({ type: 'error', text: error.message }); setBulkSaving(false); return; }
    const affected = tickets.filter(t => ids.includes(t.id));
    for (const t of affected) {
      if (t.status !== newStatus) {
        await logEvent(t.id, 'status_change', 'Support Team (Bulk)', t.status, newStatus);
        try { fetch(`${SUPABASE_URL}/functions/v1/ticket-status-email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ticket_code: t.ticket_code, status: newStatus, user_email: t.user_email, user_name: t.user_name, subject: t.subject, admin_reply: t.admin_reply ?? '' }) }).catch(() => {}); } catch { /* silent */ }
      }
    }
    setTickets(prev => prev.map(t => ids.includes(t.id) ? { ...t, status: newStatus as Ticket['status'] } : t));
    setSelectedIds(new Set()); setBulkAction('');
    setBulkMsg({ type: 'success', text: `${ids.length} ticket${ids.length > 1 ? 's' : ''} updated to "${newStatus}".` });
    setBulkSaving(false);
    setTimeout(() => setBulkMsg(null), 5000);
  };

  const toggleSelectTicket = (id: string) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => selectedIds.size === filtered.length && filtered.length > 0 ? setSelectedIds(new Set()) : setSelectedIds(new Set(filtered.map(t => t.id)));

  const filtered = tickets.filter(t =>
    (filter === 'all' || t.status === filter) &&
    (categoryFilter === 'all' || (t.category ?? 'general') === categoryFilter) &&
    (!search || t.ticket_code.toLowerCase().includes(search.toLowerCase()) || t.subject.toLowerCase().includes(search.toLowerCase()) || t.user_name?.toLowerCase().includes(search.toLowerCase()) || t.user_email?.toLowerCase().includes(search.toLowerCase()))
  );

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    answered: tickets.filter(t => t.status === 'answered').length,
    closed: tickets.filter(t => t.status === 'closed').length,
    slaBreached: tickets.filter(t => t.status === 'open' && getElapsed(t.created_at).hours >= 48).length,
  };

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, icon: 'ri-customer-service-2-line', color: 'text-slate-600 bg-slate-100' },
          { label: 'Open', value: stats.open, icon: 'ri-time-line', color: 'text-amber-500 bg-amber-50' },
          { label: 'Answered', value: stats.answered, icon: 'ri-checkbox-circle-line', color: 'text-green-600 bg-green-50' },
          { label: 'Closed', value: stats.closed, icon: 'ri-lock-line', color: 'text-gray-500 bg-gray-100' },
          { label: 'SLA Breached', value: stats.slaBreached, icon: 'ri-alarm-warning-line', color: stats.slaBreached > 0 ? 'text-red-600 bg-red-50' : 'text-gray-400 bg-gray-100' },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="bg-white rounded-xl p-4">
            <span className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${s.color}`}><i className={`${s.icon} text-base`} /></span>
            <div className={`text-2xl font-bold ${s.label === 'SLA Breached' && stats.slaBreached > 0 ? 'text-red-600' : 'text-gray-900'}`}>{loading ? '—' : s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input type="text" placeholder="Search tickets…" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full text-sm pl-8 pr-3 py-2 border border-surface-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </div>
          <div className="flex items-center gap-1 bg-white border border-surface-3 rounded-lg p-1">
            {(['all', 'open', 'answered', 'closed'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 text-xs font-medium rounded-md capitalize cursor-pointer whitespace-nowrap transition-colors ${filter === f ? 'bg-sidebar text-white' : 'text-gray-500 hover:bg-surface-2'}`}>{f}</button>
            ))}
          </div>
          {/* Category filter */}
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as 'all' | Category)}
            className="text-sm border border-surface-3 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer text-gray-600">
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{filtered.length} tickets</span>
          <button onClick={fetchTickets} disabled={loading} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-surface-3 rounded-lg text-gray-500 hover:bg-surface-2 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
        </div>
      </div>

      {/* Bulk bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="flex flex-wrap items-center gap-3 bg-brand-50 border border-brand-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center bg-brand-500 text-white rounded-full text-[10px] font-bold shrink-0">{selectedIds.size}</div>
              <span className="text-sm text-brand-700 font-medium whitespace-nowrap">{selectedIds.size} ticket{selectedIds.size > 1 ? 's' : ''} selected</span>
            </div>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <select value={bulkAction} onChange={e => setBulkAction(e.target.value as 'close' | 'answered' | '')}
                className="text-sm border border-brand-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer text-gray-700">
                <option value="">Choose action…</option>
                <option value="answered">Mark as Answered</option>
                <option value="close">Mark as Closed</option>
              </select>
              <button onClick={executeBulkAction} disabled={!bulkAction || bulkSaving}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                {bulkSaving ? <><i className="ri-loader-4-line animate-spin" />Applying…</> : <><i className="ri-check-double-line" />Apply</>}
              </button>
              <button onClick={() => { setSelectedIds(new Set()); setBulkAction(''); }} className="text-xs text-brand-600 hover:text-brand-800 cursor-pointer whitespace-nowrap">
                <i className="ri-close-line" /> Deselect all
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bulkMsg && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs border ${bulkMsg.type === 'success' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
            <i className={bulkMsg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />{bulkMsg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 border-b border-surface-3">
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={filtered.length > 0 && selectedIds.size === filtered.length} onChange={toggleSelectAll} className="rounded cursor-pointer w-4 h-4 accent-brand-500" />
              </th>
              {['Ticket ID', 'User', 'Subject', 'Category', 'Priority', 'SLA', 'Status', 'Date', 'Action'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? Array.from({ length: 4 }).map((_, i) => (
              <tr key={i} className="border-b border-surface-3 animate-pulse">
                {Array.from({ length: 10 }).map((__, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded" /></td>)}
              </tr>
            )) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-5 py-12 text-center text-sm text-gray-400">
                <i className="ri-customer-service-2-line text-3xl text-gray-200 block mb-2" />No tickets found
              </td></tr>
            ) : filtered.map((t, i) => {
              const { hours } = getElapsed(t.created_at);
              const breach = t.status === 'open' && hours >= 48;
              const cat = t.category ?? 'general';
              return (
                <motion.tr key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className={`border-b border-surface-3 last:border-0 hover:bg-surface-2 transition-colors ${selectedIds.has(t.id) ? 'bg-brand-50/50' : ''}`}>
                  <td className="px-4 py-3"><input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelectTicket(t.id)} onClick={e => e.stopPropagation()} className="rounded cursor-pointer w-4 h-4 accent-brand-500" /></td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-mono font-bold text-brand-600">{t.ticket_code}</span>
                    {t.admin_notes && <div className="mt-0.5 flex items-center gap-1 text-[10px] text-amber-600"><i className="ri-sticky-note-line" />Has notes</div>}
                  </td>
                  <td className="px-4 py-3"><div className="text-xs font-medium text-gray-800">{t.user_name}</div><div className="text-[11px] text-gray-400">{t.user_email}</div></td>
                  <td className="px-4 py-3 text-xs text-gray-700 max-w-[160px] truncate">{t.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${categoryColor[cat]}`}>
                      <i className={`${categoryIcon[cat]} text-[10px]`} />{cat}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${priorityColor[t.priority]}`}>{t.priority}</span>
                    {t.priority === 'urgent' && <div className="text-[9px] text-red-400 mt-0.5">Auto-escalated</div>}
                  </td>
                  <td className="px-4 py-3">
                    <SLABadge ticket={t} />
                    {breach && <div className="text-[10px] text-red-500 font-medium mt-0.5 flex items-center gap-0.5"><i className="ri-alarm-warning-fill" />Breach</div>}
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColor[t.status]}`}>{t.status}</span></td>
                  <td className="px-4 py-3 text-[11px] text-gray-400 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openTicket(t)} className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 cursor-pointer whitespace-nowrap font-medium">
                        <i className="ri-reply-line" />Open
                      </button>
                      {t.user_comment && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" title="User replied" />}
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-surface-3 shrink-0">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900 truncate max-w-xs">{selected.subject}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColor[selected.status]}`}>{selected.status}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${categoryColor[selected.category ?? 'general']}`}>
                      <i className={`${categoryIcon[selected.category ?? 'general']} text-[10px]`} />{selected.category ?? 'general'}
                    </span>
                    <SLABadge ticket={selected} />
                  </div>
                  <p className="text-[11px] font-mono text-gray-400 mt-0.5">{selected.ticket_code} · {selected.user_name} &lt;{selected.user_email}&gt;</p>
                </div>
                <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer shrink-0 ml-2">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex items-center border-b border-surface-3 px-6 shrink-0">
                {([
                  { id: 'reply', label: 'Reply', icon: 'ri-reply-line' },
                  { id: 'notes', label: 'Internal Notes', icon: 'ri-sticky-note-line' },
                  { id: 'timeline', label: 'Timeline', icon: 'ri-history-line' },
                ] as const).map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 -mb-px transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.id ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                    <i className={tab.icon} />{tab.label}
                    {tab.id === 'notes' && selected.admin_notes && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">

                {/* ── Timeline ── */}
                {activeTab === 'timeline' && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Full Activity Log</h4>
                    <ActivityTimeline ticketId={selected.id} />
                  </div>
                )}

                {/* ── Internal Notes ── */}
                {activeTab === 'notes' && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <i className="ri-eye-off-line text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-amber-800">Admin-only — never shown to the user</p>
                        <p className="text-[11px] text-amber-700 mt-0.5">Use this to leave internal context, follow-up reminders, or team escalation notes.</p>
                      </div>
                    </div>
                    <textarea
                      className="w-full text-sm border border-surface-3 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                      rows={8}
                      placeholder="Add internal notes here… e.g. 'Awaiting billing confirmation', 'Escalated to dev team'"
                      value={adminNotesText}
                      onChange={e => { if (e.target.value.length <= 1000) setAdminNotesText(e.target.value); }}
                      maxLength={1000}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-gray-400">{adminNotesText.length}/1000</span>
                      <AnimatePresence>
                        {notesMsg && (
                          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className={`text-xs flex items-center gap-1 ${notesMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                            <i className={notesMsg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />{notesMsg.text}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                    <button onClick={saveNotes} disabled={notesSaving}
                      className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-colors">
                      {notesSaving ? <><i className="ri-loader-4-line animate-spin" />Saving…</> : <><i className="ri-save-line" />Save Internal Notes</>}
                    </button>
                  </div>
                )}

                {/* ── Reply ── */}
                {activeTab === 'reply' && (
                  <>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-sidebar rounded-full flex items-center justify-center"><i className="ri-user-line text-white text-[10px]" /></div>
                        <span className="text-xs font-semibold text-gray-700">User: {selected.user_name}</span>
                        <span className="text-[11px] text-gray-400 ml-auto">{new Date(selected.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{selected.message}</p>
                    </div>

                    {selected.user_comment && (
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center"><i className="ri-reply-line text-white text-[10px]" /></div>
                          <span className="text-xs font-semibold text-amber-800">User Follow-up Reply</span>
                          <span className="text-[11px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full ml-auto font-medium">New</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{selected.user_comment}</p>
                      </div>
                    )}

                    {selected.admin_reply && (
                      <div className="bg-brand-50 border border-brand-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center"><i className="ri-customer-service-2-line text-white text-[10px]" /></div>
                          <span className="text-xs font-semibold text-brand-700">Your Previous Reply</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{selected.admin_reply}</p>
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">{selected.admin_reply ? 'Update / Edit Reply' : 'Write Admin Reply'}</label>
                      <textarea className="w-full text-sm border border-surface-3 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                        rows={5} placeholder="Type your reply to the user…" value={replyText}
                        onChange={e => { if (e.target.value.length <= 500) setReplyText(e.target.value); }} maxLength={500} />
                      <div className="text-[11px] text-gray-400 text-right mt-0.5">{replyText.length}/500</div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-2 block">Update Ticket Status</label>
                      <div className="grid grid-cols-3 gap-2">
                        {([
                          { key: 'open', label: 'Open', icon: 'ri-time-line', active: 'bg-amber-500 text-white border-amber-500' },
                          { key: 'answered', label: 'Answered', icon: 'ri-checkbox-circle-line', active: 'bg-green-500 text-white border-green-500' },
                          { key: 'closed', label: 'Closed', icon: 'ri-lock-line', active: 'bg-gray-600 text-white border-gray-600' },
                        ] as const).map(s => (
                          <button key={s.key} onClick={() => setReplyStatus(s.key)}
                            className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap border transition-all ${replyStatus === s.key ? s.active : 'border-surface-3 text-gray-500 hover:border-gray-300 bg-white'}`}>
                            <i className={s.icon} />{s.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <AnimatePresence>
                      {msg && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs border ${msg.type === 'success' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                          <i className={msg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />{msg.text}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-surface-3 shrink-0 flex gap-3">
                <button onClick={() => setSelected(null)} className="flex-1 py-2.5 border border-surface-3 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Close</button>
                {activeTab === 'reply' && (
                  <button onClick={saveReply} disabled={saving}
                    className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    {saving ? <><i className="ri-loader-4-line animate-spin" />Saving…</> : <><i className="ri-save-line" />Save Reply &amp; Status</>}
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
